import { HomeLoaderData } from '../../../types/homeLoaderData';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useRouteLoaderData } from 'react-router-dom';

import SVGIcon, { IIcons } from '../../atoms/icons';
import Text from '../../atoms/text';
import classNames from 'classnames';
import pacote from '../../../../package.json';

interface SidebarProps {
	isSidebarVisible: boolean;
	setIsSidebarVisible: React.Dispatch<React.SetStateAction<boolean>>;
}

interface SidebarItemProps {
	icon: IIcons;
	label: string;
	path: string;
	isActive: boolean;
	novaAba?: boolean;
	onClick: () => void;
}

const SidebarItem = ({
	icon,
	label,
	path,
	isActive,
	novaAba,
	onClick,
}: SidebarItemProps) => (
	<li>
		<Link
			to={path}
			className={classNames(
				'flex items-center px-3 py-2 gap-3 rounded-md transition-colors cursor-pointer',
				{
					'bg-white text-primary': isActive,
					'bg-primary hover:bg-primary-dark text-white': !isActive,
				}
			)}
			onClick={onClick}
			aria-current={isActive ? 'page' : undefined}
			rel={novaAba ? 'noreferrer' : undefined}
			target={novaAba ? '_blank' : undefined}
		>
			<div
				className={classNames('p-2 rounded-md', {
					'bg-primary-light': isActive,
				})}
			>
				<SVGIcon className="w-6 h-auto fill-white" icon={icon} />
			</div>
			<Text className="font-semibold select-none">{label}</Text>
		</Link>
	</li>
);

const Sidebar = ({ isSidebarVisible, setIsSidebarVisible }: SidebarProps) => {
	const location = useLocation();
	const { t } = useTranslation();

	const { user } = useRouteLoaderData('home') as HomeLoaderData;

	const [paths, setPaths] = useState<
		{ icon: string; label: string; path: string; novaAba?: boolean }[]
	>([]);

	useEffect(() => {
		if (!user) return;

		const loadItems: {
			icon: string;
			label: string;
			path: string;
			novaAba?: boolean;
		}[] = [{ icon: 'Home', label: t('home.title'), path: '/' }];

		const fluxoUnicoAtivado = user.client?.system_configs?.processoFluxoUnicoAtivado === true;
		const scrollBackAtivado = user.client?.system_configs?.moduloAuditoriaAtivado === true;

		if (user.type !== 'User') {
			loadItems.push(
				{
					icon: 'Monitor',
					label: t('monitor.title'),
					path: '/monitor',
				},
				{
					icon: 'Search',
					label: t('queries.title'),
					path: '/queries',
				}
			);

			if (user.type !== 'Client') {
				loadItems.push({
					icon: 'RewindCircle',
					label: 'Container Tracking',
					path: '/container-tracking?container=',
					novaAba: true,
				});

				loadItems.push({
					icon: 'Scroll',
					label: 'Auditoria',
					path: '/container_auditoria',
				});
			}
		} else {
			loadItems.push({
				icon: 'Bot',
				label: 'Chatbot',
				path: '/chatbot',
			});

			if (
				user.permissions.isSystemAdmin ||
				user.permissions.isMonitoring
			) {
				loadItems.push({
					icon: 'Monitor',
					label: t('monitor.title'),
					path: '/monitor',
				});
			}

			if (
				user.permissions.isSystemAdmin ||
				user.permissions.isLocalAdmin
			) {
				loadItems.push(
					{
						icon: 'Anchor',
						label: t('port_management.title'),
						path: '/port_management',
					},
					{ icon: 'Admin', label: t('admin.title'), path: '/admin' }
				);
			}

			if (user.permissions.isSystemAdmin || user.permissions.isMovement) {
				loadItems.push({
					icon: 'Movement',
					label: t('movement.title'),
					path: '/movement',
				});
			}

			if (
				user.permissions.isSystemAdmin ||
				user.permissions.isMaintenance
			) {
				loadItems.push({
					icon: 'Maintenance',
					label: t('maintenance.title'),
					path: '/maintenance',
				});
			}

			loadItems.push({
				icon: 'Search',
				label: t('queries.title'),
				path: '/queries',
			});

			if (user.permissions.isSystemAdmin) {
				loadItems.push({
					icon: 'Report',
					label: t('reports.title'),
					path: '/reports',
				});
			}
			if (user.permissions.isSystemAdmin) {
				loadItems.push({
					icon: 'Financial',
					label: t('financial.title'),
					path: '/financial',
				});
			}

			if (fluxoUnicoAtivado) {
				loadItems.push({
					icon: 'RewindCircle',
					label: 'Container Tracking',
					path: '/container-tracking?container=',
					novaAba: true,
				});
			}

			if (scrollBackAtivado) {
				loadItems.push({
					icon: 'Scroll',
					label: 'Auditoria',
					path: '/container_auditoria',
				});
			}

			loadItems.push({
				icon: 'Help',
				label: t('help.title'),
				path: '/help',
			});
		}

		setPaths(loadItems);
	}, [user]);

	const isPathActive = (path: string) =>
		path === '/'
			? location.pathname === path
			: location.pathname.startsWith(path);

	const closeSidebar = () => setIsSidebarVisible(false);

	return (
		<>
			{/* Overlay */}
			<div
				className={classNames(
					'fixed top-0 left-0 w-full h-full bg-black bg-opacity-50 z-40 transition-opacity',
					{ hidden: !isSidebarVisible }
				)}
				onClick={closeSidebar}
			></div>

			{/* Sidebar */}
			<div
				className={classNames(
					'fixed flex flex-col top-0 left-0 z-50 h-full w-full sm:w-80 p-4 space-y-6 overflow-auto bg-primary text-white transform transition-transform duration-300 ease-in-out',
					{
						'translate-x-0': isSidebarVisible,
						'-translate-x-full': !isSidebarVisible,
					}
				)}
			>
				{/* Header */}
				<div className="flex justify-between items-center">
					<SVGIcon className="h-10 w-auto" icon="Logo" />
					<SVGIcon
						className="h-5 w-auto cursor-pointer"
						icon="MenuClose"
						onClick={closeSidebar}
					/>
				</div>

				{user.type !== 'User' && (
					<Text className="text-white text-xl font-bold mt-8">
						{t('home.client')}
					</Text>
				)}

				{/* Navigation Items */}
				<ul className="flex flex-col">
					{paths.map(({ icon, label, path, novaAba }) => (
						<SidebarItem
							key={path}
							icon={icon as IIcons}
							label={label}
							path={path}
							isActive={isPathActive(path)}
							novaAba={novaAba}
							onClick={closeSidebar}
						/>
					))}
				</ul>
				<div className="flex flex-1 items-end justify-end">
					<Text className="text-xs text-white">v{pacote.version}</Text>
				</div>
			</div>
		</>
	);
};

export default Sidebar;
